import { Directive, ElementRef, Input } from '@angular/core';

/**
 * @directive ElementTemplateDirective
 * @selector [appElementTemplate]
 * @description
 * Angular directive to associate a DOM element (host) with a logical template name, enabling dynamic referencing and manipulation of elements in advanced UI layouts.
 * Useful for dynamic content projection, custom layout engines, or when you need to register and later retrieve a specific element by name.
 *
 * @example
 * <div appElementTemplate="header"></div>
 * <section [appElementTemplate]="'footer'"></section>
 *
 * @template THost The type of the host element (defaults to HTMLElement).
 */
@Directive({
  selector: '[appElementTemplate]'
})
export class ElementTemplateDirective<THost = HTMLElement> {

  /**
   * The logical name for this element template. Used to reference or query the element in code.
   *
   * @input appElementTemplate
   * @type {string}
   * @example
   * <div appElementTemplate="sidebar"></div>
   */
  @Input('appElementTemplate')
  public name: string = '';

  /**
   * Returns the native HTML element associated with this directive, or null if not an HTMLElement.
   *
   * @readonly
   * @type {THost | null}
   * @example
   * const el = directiveInstance.htmlElement;
   */
  public get htmlElement(): THost | null {
    return this.element.nativeElement instanceof HTMLElement ? this.element.nativeElement : null;
  }

  /**
   * Creates an instance of ElementTemplateDirective.
   *
   * @param element Reference to the host element.
   */
  constructor(public element: ElementRef<THost>) { }

}
